import { useEffect, useMemo, useRef, useState } from 'react';
import {
	Button,
	Card,
	Dimmer,
	Grid,
	GridColumn,
	Loader,
	Modal,
	Sticky,
} from 'semantic-ui-react';
import { useReactToPrint } from 'react-to-print';
import emailjs from '@emailjs/browser';
import {
	APP_HEADER,
	EMAIL_BUTTON_TEXT,
	MESSAGE_ERROR_BODY,
	MESSAGE_ERROR_HEADER,
	MESSAGE_SUCCESS_BODY,
	MESSAGE_SUCCESS_HEADER,
	PRINT_BUTTON_TEXT,
	RESET_BUTTON_TEXT,
} from '../constants/strings';
import Disclaimer from './Disclaimer';
import { useWindowSize } from '../hooks';
import { useStore } from '../store/useStore';
import SellerForm from './SellerForm';
import { formatCurrency } from '../utils/formatCurrency';
import withState from './WithState';
import emailTemplate from '../utils/emailTemplate';
import selectors from '../store/selectors';
import PrintOut from './PrintOut';
import { titleRateLookup } from '../utils/titleRateLookup';

const SellerNetForm = (props: any) => {
	const objRef = useRef(null);
	const componentRef = useRef<HTMLDivElement>(null);
	const window = useWindowSize();
	const store = useStore((state) => ({
		setTableReference: state.setTableReference,
		setTitleInsurance: state.setTitleInsurance,
		loanPayoffs: state.loanPayoffs,
		estimatedTotalCosts: state.estimatedTotalCosts,
		estimatedNetProceeds: state.estimatedNetProceeds,
		salesPrice: state.salesPrice.raw,
		resetState: state.resetState,
	}));

	const [stickyActive, setStickyActive] = useState(true);

	useEffect(() => {
		const titleRate: any = titleRateLookup(props.records, store.salesPrice);
		store.setTitleInsurance(titleRate);
	}, [store.salesPrice]);

	useEffect(() => {
		store.setTableReference();
	}, []);

	useEffect(() => {
		if (window.width < 765) {
			// If screen size is small enough remove position: sticky
			setStickyActive(false);
		} else {
			setStickyActive(true);
		}
	}, [window.width]);

	const handlePrint = useReactToPrint({
		content: () => componentRef.current,
	});

	const sendEmail = () => {
		const template_params = {
			user_name: props.firstName + ' ' + props.lastName,
			user_email: props.email,
			notes: 'Check this out!',
			message_html: emailTemplate(store),
		};
		props.setLoader(true);
		emailjs
			.send(
				'service_m2604nx',
				'kootenai_template',
				template_params,
				'user_bFjld4MhR9C6g1l82iRp5'
			)
			.then(
				() => {
					props.setModalVisibility();
					props.setModalMessage(MESSAGE_SUCCESS_HEADER, MESSAGE_SUCCESS_BODY);
				},
				() => {
					props.setModalVisibility();
					props.setModalMessage(MESSAGE_ERROR_HEADER, MESSAGE_ERROR_BODY);
				}
			);
	};

	const stickyComponent = useMemo(
		() => (
			<div ref={objRef} style={{ height: '100%' }}>
				<Sticky active={stickyActive} context={objRef} offset={35}>
					<Card className={'kootenai-card'} fluid>
						<Card.Content style={{ padding: 30 }}>
							<Card.Header>Net Proceeds</Card.Header>
							<h6 className={'big'}>
								{formatCurrency(store.estimatedNetProceeds)}
							</h6>
							<Grid columns={2}>
								<Grid.Column>
									<p>Total costs</p>
									<h6 className={'light-text'}>
										{formatCurrency(store.estimatedTotalCosts)}
									</h6>
								</Grid.Column>
								<Grid.Column>
									<p>Total loan payoffs</p>
									<h6 className={'light-text'}>
										{formatCurrency(store.loanPayoffs.raw)}
									</h6>
								</Grid.Column>
							</Grid>
						</Card.Content>
					</Card>
				</Sticky>
			</div>
		),
		[
			stickyActive,
			store.loanPayoffs,
			store.estimatedTotalCosts,
			store.estimatedNetProceeds,
		]
	);

	return (
		<Grid stackable columns={2} className={'kootenai-form-wrap'}>
			<GridColumn width={6} className={'kootenai-flex-left'}>
				<div className={'kootenai-form-padding'}>
					<h2 className={'kootenai-app-header'}>{APP_HEADER}</h2>
					<SellerForm />

					{!stickyActive ? stickyComponent : null}
					<Disclaimer />
					<div className={'kootenai-buttons-container'}>
						<Button
							className={'kootenai-button'}
							onClick={sendEmail}
							color={'green'}
						>
							{EMAIL_BUTTON_TEXT}
						</Button>

						<Button
							className={'kootenai-button'}
							onClick={handlePrint as any}
							color={'blue'}
						>
							{PRINT_BUTTON_TEXT}
						</Button>

						<Button
							className={'kootenai-button'}
							onClick={() => store.resetState()}
							color={'red'}
						>
							{RESET_BUTTON_TEXT}
						</Button>
					</div>
				</div>
			</GridColumn>
			{stickyActive ? (
				<GridColumn width={6} className={'kootenai-flex-right'}>
					{stickyComponent}
				</GridColumn>
			) : null}

			<Dimmer className="vtg-dimmer" active={props.loading}>
				<Loader>{props.loaderText}</Loader>
			</Dimmer>
			<Modal
				open={props.modalVisibility}
				onClose={() => props.setModalVisibility()}
				size="small"
			>
				<Modal.Content>
					<h3>{props.modalMessage.header}</h3>

					<p>{props.modalMessage.body}</p>

					<Button color="blue" onClick={() => props.setModalVisibility()}>
						Continue
					</Button>
				</Modal.Content>
			</Modal>

			<div style={{ display: 'none' }}>
				<PrintOut ref={componentRef} />
			</div>
		</Grid>
	);
};

export default withState(selectors)(SellerNetForm);
